import { init } from '@instantdb/react';
import schema from '../../instant.schema';

/**
 * Resolve the InstantDB app id from one of three sources, in order of preference:
 *   1. `<script src="…/widget.js" data-app="…">` attribute
 *   2. `window.UINOTER_APP_ID` global override
 *   3. `import.meta.env.VITE_INSTANT_APP_ID` (dev fallback)
 *
 * If none is present we DON'T throw at module load — that would kill the
 * entire bundle before main.tsx can register the keydown listener and the
 * `window.uinoter` debug global. Instead we return `null` and let main.tsx
 * surface a visible error toast.
 */
export function resolveAppId(): string | null {
  if (typeof document !== 'undefined') {
    const cs = document.currentScript as HTMLScriptElement | null;
    const fromCurrent = cs?.dataset?.app;
    if (fromCurrent) return fromCurrent;

    const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[data-app]'));
    for (const s of scripts) {
      if (/widget(\.[a-z0-9]+)?\.js/i.test(s.src) || s.dataset.uinoter === 'true') {
        if (s.dataset.app) return s.dataset.app;
      }
    }
  }

  const fromWindow = (typeof window !== 'undefined'
    ? (window as unknown as { UINOTER_APP_ID?: string }).UINOTER_APP_ID
    : undefined);
  if (fromWindow) return fromWindow;

  const fromEnv = import.meta.env.VITE_INSTANT_APP_ID as string | undefined;
  if (fromEnv) return fromEnv;

  return null;
}

const APP_ID = resolveAppId();

/**
 * Real InstantDB client when the embed is configured correctly.
 *
 * If no app id was provided we still expose a `db` symbol so the rest of the
 * codebase stays statically typed — but it is a stub that throws on access.
 * `dbAvailable` is the runtime flag to gate any rendering that would touch it.
 * `main.tsx` checks this and shows a visible error toast instead of mounting
 * the React app.
 */
const realDb = APP_ID ? init({ appId: APP_ID, schema }) : null;

export const dbAvailable = realDb !== null;
export const appId = APP_ID;

type DBType = ReturnType<typeof init<typeof schema>>;
export type DB = DBType;

export const db: DB = (realDb ?? new Proxy({} as DB, {
  get() {
    throw new Error('UI Noter: db accessed before configuration. Pass data-app on the script tag.');
  },
}));
